import type { GearItem, GearOwner } from "@highmarch/domain";
import { motionOk } from "../kit";
import type { ArmouryCtx } from "./host";
import { gearDisc, h, ownerName } from "./itemBits";

/** The item surfaces' feedback: the equip / upgrade burst, the icon flight, the shine, the pop and the Mythic banner. */

const FLY_MS = 240;
const BURST_MS = 420;
const SPARKS = 4;
const SPARK_REACH_PX = 26;
const SHINE_MS = 520;
const POP_MS = 260;

const canAnimate = (e: Element): boolean => motionOk() && typeof (e as HTMLElement).animate === "function";
const rectOf = (e: Element) => (typeof e.getBoundingClientRect === "function" ? e.getBoundingClientRect() : null);

/** A ring in the item's rarity colour and four sparks bursting from `target` (equip landing, upgrade). */
export function burst(target: HTMLElement, rarity: number): void {
  if (!canAnimate(target)) return;
  const ring = h("span", `aburst r${rarity}`);
  target.append(ring);
  ring.animate([{ transform: "scale(0.6)", opacity: 0.95 }, { transform: "scale(1.5)", opacity: 0 }], { duration: BURST_MS, easing: "ease-out" }).onfinish = () => ring.remove();
  for (let i = 0; i < SPARKS; i++) {
    const s = h("span", "aspark");
    target.append(s);
    const a = (Math.PI / 2) * i + Math.PI / 4;
    s.animate([{ transform: "translate(-50%,-50%) rotate(45deg) scale(1)", opacity: 1 },
      { transform: `translate(calc(-50% + ${Math.cos(a) * SPARK_REACH_PX}px), calc(-50% + ${Math.sin(a) * SPARK_REACH_PX}px)) rotate(45deg) scale(0.3)`, opacity: 0 }],
    { duration: BURST_MS, easing: "ease-out" }).onfinish = () => s.remove();
  }
  target.animate([{ transform: "scale(1)" }, { transform: "scale(0.9)", offset: 0.25 }, { transform: "scale(1.08)", offset: 0.6 }, { transform: "scale(1)" }], { duration: BURST_MS, easing: "ease-out" });
}

/** Flies a copy of `from` (an icon) onto `to` (FLIP). Calls `done` when it lands (at once without motion). */
export function flyTo(host: HTMLElement, from: HTMLElement, to: HTMLElement, done: () => void): void {
  const a = rectOf(from), b = rectOf(to), hr = rectOf(host);
  if (!canAnimate(host) || a === null || b === null || hr === null || a.width === 0 || b.width === 0) { done(); return; }
  const ghost = from.cloneNode(true) as HTMLElement;
  ghost.classList.add("afly");
  ghost.style.left = `${a.left - hr.left}px`;
  ghost.style.top = `${a.top - hr.top}px`;
  ghost.style.width = `${a.width}px`;
  ghost.style.height = `${a.height}px`;
  host.append(ghost);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const s = Math.max(0.3, Math.min(1.2, b.width / a.width));
  ghost.animate([{ transform: "translate(0,0) scale(1)" }, { transform: `translate(${dx}px, ${dy - 24}px) scale(${(1 + s) / 2})`, offset: 0.55 }, { transform: `translate(${dx}px, ${dy}px) scale(${s})` }],
    { duration: FLY_MS, easing: "cubic-bezier(.3,.7,.4,1)" }).onfinish = () => { ghost.remove(); done(); };
}

/** A shine sweeping across an icon (upgrade). */
export function shine(target: HTMLElement): void {
  if (!canAnimate(target)) return;
  // The clip span keeps the band on the disc; the band itself is what moves (transform only).
  const band = h("i");
  const s = h("span", "ashine", band);
  target.append(s);
  band.animate([{ transform: "translateX(-120%) skewX(-20deg)" }, { transform: "translateX(220%) skewX(-20deg)" }], { duration: SHINE_MS, easing: "ease-out" }).onfinish = () => s.remove();
}

/** A small pop on a number or pip. */
export function pop(target: Element | null): void {
  if (target === null || !canAnimate(target)) return;
  (target as HTMLElement).animate([{ transform: "scale(1)" }, { transform: "scale(1.35)", offset: 0.35 }, { transform: "scale(1)" }], { duration: POP_MS, easing: "ease-out" });
}

const MYTHIC_BANNER_MS = 2800;

/** The Mythic moment: a name banner across the sheet when an equipped Mythic effect acts on its owner. */
export function celebrateMythic(ctx: ArmouryCtx, it: GearItem, owner: GearOwner): void {
  const def = ctx.B.gear.mythicEffects[it.mythicEffect];
  if (def === undefined) return;
  for (const old of Array.from(ctx.sheet.querySelectorAll(".amythic"))) old.remove();
  const el = h("div", "amythic", h("small", "", "MYTHIC AWAKENS"), gearDisc(ctx.B, it, "adisc sm"),
    h("b", "disp", def.name), h("span", "", `${def.description} · on ${ownerName(ctx.B, owner)}`));
  el.setAttribute("role", "status");
  el.addEventListener("click", () => el.remove());
  ctx.sheet.append(el);
  if (motionOk()) el.classList.add("go");
  window.setTimeout(() => el.remove(), MYTHIC_BANNER_MS);
}

const LEVEL_UP_MS = 900;
const LEVEL_UP_RISE_PX = 36;
const LEVEL_UP_SPARKS = 8;
const LEVEL_UP_REACH_PX = 46;

/**
 * The level-up flourish (gear v2's Raise): a gold ring and a crown of sparks off the item's disc, a shine across it,
 * and the new level rising off it in display type ("LV 35", or "LV 34 → 38" for Raise max). Transform/opacity only;
 * under reduced motion the card's numbers still change, nothing moves.
 */
export function levelUp(target: HTMLElement, text: string, rarity: number): void {
  if (!canAnimate(target)) return;
  burst(target, rarity);
  shine(target);
  const ring = h("span", "alvring");
  target.append(ring);
  ring.animate([{ transform: "translate(-50%,-50%) scale(0.4)", opacity: 1 }, { transform: "translate(-50%,-50%) scale(1.9)", opacity: 0 }], { duration: LEVEL_UP_MS * 0.7, easing: "cubic-bezier(.2,.8,.3,1)" }).onfinish = () => ring.remove();
  for (let i = 0; i < LEVEL_UP_SPARKS; i++) {
    const s = h("span", "alvspark");
    target.append(s);
    const a = (2 * Math.PI * i) / LEVEL_UP_SPARKS - Math.PI / 2;
    const r = LEVEL_UP_REACH_PX * (i % 2 === 0 ? 1 : 0.7);
    s.animate([{ transform: "translate(-50%,-50%) scale(1)", opacity: 1 },
      { transform: `translate(calc(-50% + ${Math.cos(a) * r}px), calc(-50% + ${Math.sin(a) * r}px)) scale(0.2)`, opacity: 0 }],
    { duration: LEVEL_UP_MS * 0.6, delay: 60, easing: "cubic-bezier(.2,.8,.3,1)", fill: "backwards" }).onfinish = () => s.remove();
  }
  const f = h("span", "alvup disp", text);
  target.append(f);
  f.animate([{ transform: "translate(-50%, 0) scale(0.6)", opacity: 0 }, { transform: "translate(-50%, -18px) scale(1.15)", opacity: 1, offset: 0.25 },
    { transform: `translate(-50%, -${LEVEL_UP_RISE_PX}px) scale(1)`, opacity: 0 }], { duration: LEVEL_UP_MS, easing: "ease-out" }).onfinish = () => f.remove();
}
